import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash2, Search, ArrowLeft, Sparkles, Link2, Copy, GitMerge, RefreshCw } from 'lucide-react';
import UploadResourceListModal from '../../components/UploadResourceListModal';

const EMPTY = {
  name: '', category_slug: '', description: '', phone: '', website: '',
  address: '', city: '', state: '', zip_code: '', hours: '', eligibility: '',
  languages: '', tags: [], is_national: false,
};

const AdminResources = () => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'review' | 'claims' | 'imports'
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewCounts, setReviewCounts] = useState({});
  const [claims, setClaims] = useState([]);
  const [claimCounts, setClaimCounts] = useState({});
  const [imports, setImports] = useState([]);
  const [linkIssues, setLinkIssues] = useState([]);
  const [linkMeta, setLinkMeta] = useState({});
  const [dupPairs, setDupPairs] = useState([]);
  const [activeSel, setActiveSel] = useState(new Set());
  const [bulkCat, setBulkCat] = useState('');
  const [busy, setBusy] = useState(false);
  const toggleActive = (id) => setActiveSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [linkSel, setLinkSel] = useState(new Set());
  const toggleLinkSel = (id) => setLinkSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleLinkSelAll = () => setLinkSel((prev) => prev.size === linkIssues.length ? new Set() : new Set(linkIssues.map((r) => r.resource_id)));
  const toggleSelect = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelectedIds((prev) => prev.size === reviewItems.length ? new Set() : new Set(reviewItems.map((r) => r.resource_id)));

  // Group the review queue by assigned category; missing category -> "Uncategorized".
  const reviewGroups = useMemo(() => {
    const map = new Map();
    for (const r of reviewItems) {
      const key = (r.category_slug && r.category) ? r.category : 'Uncategorized';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries())
      .map(([label, items]) => ({ label, items }))
      .sort((a, b) => (a.label === 'Uncategorized' ? 1 : b.label === 'Uncategorized' ? -1 : a.label.localeCompare(b.label)));
  }, [reviewItems]);

  const toggleCategory = (items) => setSelectedIds((prev) => {
    const ids = items.map((r) => r.resource_id);
    const allSel = ids.every((id) => prev.has(id));
    const n = new Set(prev);
    ids.forEach((id) => allSel ? n.delete(id) : n.add(id));
    return n;
  });

  const runBulk = async (ids, action, label) => {
    ids = Array.from(new Set(ids)).filter(Boolean);
    if (!ids.length) return;
    const scope = label ? ` in "${label}"` : '';
    if (action === 'reject') {
      if (!window.confirm(`Reject ${ids.length} resource(s)${scope}? This cannot be undone.`)) return;
    }
    let reason = '';
    if (action === 'reject') reason = window.prompt('Reason for rejecting (optional):') ?? '';
    try {
      const { data } = await axios.post(`${API_URL}/admin/resources/bulk`, { ids, action, reason });
      const set = new Set(ids);
      setReviewItems((prev) => prev.filter((r) => !set.has(r.resource_id)));   // live update, no refetch flash
      setSelectedIds((prev) => { const n = new Set(prev); ids.forEach((id) => n.delete(id)); return n; });
      toast.success(data.message || `${action === 'approve' ? 'Approved' : 'Rejected'} ${ids.length}.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed.'); }
  };
  const bulkAction = (action) => runBulk(Array.from(selectedIds), action);

  const loadReview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/resources/review-queue`);
      setReviewItems(res.data.resources || []);
      setReviewCounts(res.data.counts || {});
      setCategories((c) => (c.length ? c : res.data.categories || []));
    } catch (e) { toast.error('Could not load review queue (admin only).'); } finally { setLoading(false); }
  }, []);

  const loadClaims = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/resource-claims`);
      setClaims(res.data.claims || []);
      setClaimCounts(res.data.counts || {});
    } catch (e) { toast.error('Could not load claims.'); } finally { setLoading(false); }
  }, []);

  const loadImports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/resource-imports`);
      setImports(res.data.imports || []);
    } catch (e) { toast.error('Could not load import history.'); } finally { setLoading(false); }
  }, []);

  const loadLinkIssues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/resources/link-issues`);
      setLinkIssues(res.data.resources || []);
      setLinkMeta({ last_run: res.data.last_run, count: res.data.count, broken_count: res.data.broken_count, review_count: res.data.review_count });
      setLinkSel(new Set());
    } catch (e) { toast.error('Could not load link issues.'); } finally { setLoading(false); }
  }, []);

  const recheckLink = async (id) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/resources/${id}/recheck-link`);
      setLinkIssues((prev) => prev.map((r) => r.resource_id === id
        ? { ...r, link_status: data.link_status, link_http_status: data.link_http_status, link_reason: data.link_reason, link_checked_at: data.checked_at, needs_manual_review: data.link_status === 'blocked' }
        : r).filter((r) => r.resource_id !== id || data.link_status !== 'ok'));
      toast.success(data.link_status === 'ok' ? 'Link is reachable — removed from issues.' : `Rechecked: ${data.link_reason}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Recheck failed.'); } finally { setBusy(false); }
  };

  const verifyLink = async (id) => {
    try {
      await axios.post(`${API_URL}/admin/resources/${id}/verify-link`);
      setLinkIssues((prev) => prev.filter((r) => r.resource_id !== id));
      setLinkSel((prev) => { const n = new Set(prev); n.delete(id); return n; });
      toast.success('Verified — kept in the public directory.');
    } catch (e) { toast.error('Could not verify.'); }
  };

  const deleteLinkResource = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This permanently removes the resource. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/resources/${id}`);
      setLinkIssues((prev) => prev.filter((r) => r.resource_id !== id));
      setLinkSel((prev) => { const n = new Set(prev); n.delete(id); return n; });
      toast.success('Resource deleted.');
    } catch (e) { toast.error('Could not delete.'); }
  };

  const bulkLink = async (action) => {
    const ids = Array.from(linkSel);
    if (!ids.length) { toast.error('Select at least one resource.'); return; }
    if (action === 'delete' && !window.confirm(`Permanently delete ${ids.length} resource(s)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/resources/bulk-link`, { ids, action });
      toast.success(data.message || 'Done.');
      await loadLinkIssues();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed.'); } finally { setBusy(false); }
  };

  const loadDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/resources/duplicates`);
      setDupPairs(res.data.pairs || []);
    } catch (e) { toast.error('Could not load duplicates.'); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (viewMode === 'review') loadReview();
    else if (viewMode === 'claims') loadClaims();
    else if (viewMode === 'imports') loadImports();
    else if (viewMode === 'links') loadLinkIssues();
    else if (viewMode === 'dupes') loadDuplicates();
  }, [viewMode, loadReview, loadClaims, loadImports, loadLinkIssues, loadDuplicates]);

  const approveClaim = async (id) => {
    try { await axios.post(`${API_URL}/admin/resource-claims/${id}/approve`); toast.success('Claim approved & listing updated.'); loadClaims(); }
    catch (e) { toast.error('Could not approve claim.'); }
  };
  const rejectClaim = async (id) => {
    const reason = window.prompt('Reason for rejecting (optional):') ?? '';
    try { await axios.post(`${API_URL}/admin/resource-claims/${id}/reject`, { reason }); toast.success('Claim rejected.'); loadClaims(); }
    catch (e) { toast.error('Could not reject claim.'); }
  };
  const bulkApproveImport = async (batchId) => {
    if (!window.confirm('Approve all non-duplicate pending resources from this upload?')) return;
    try {
      const res = await axios.post(`${API_URL}/admin/resource-imports/${batchId}/approve-non-duplicates`);
      toast.success(res.data.message || 'Approved.');
      loadImports();
    } catch (e) { toast.error('Could not bulk approve.'); }
  };

  const approveResource = async (id) => {
    try { await axios.post(`${API_URL}/admin/resources/${id}/approve`); toast.success('Approved & published.'); setReviewItems((prev) => prev.filter((r) => r.resource_id !== id)); }
    catch (e) { toast.error('Could not approve.'); }
  };
  const rejectResource = async (id) => {
    const reason = window.prompt('Reason for rejecting (optional):') ?? '';
    try { await axios.post(`${API_URL}/admin/resources/${id}/reject`, { reason }); toast.success('Rejected.'); setReviewItems((prev) => prev.filter((r) => r.resource_id !== id)); }
    catch (e) { toast.error('Could not reject.'); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (catFilter !== 'all') q.set('category', catFilter);
      if (search) q.set('search', search);
      const res = await axios.get(`${API_URL}/admin/resources?${q.toString()}`);
      setResources(res.data.resources || []);
      setCategories(res.data.categories || []);
    } catch (e) { toast.error('Could not load (admin only).'); } finally { setLoading(false); }
  }, [catFilter, search]);

  useEffect(() => { document.title = 'Manage Resources | Admin'; load(); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm({ ...EMPTY, category_slug: categories[0]?.slug || '' }); setOpen(true); };
  const openEdit = (r) => { setEditing(r.resource_id); setForm({ ...EMPTY, ...r, tags: r.tags || [] }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.category_slug) { toast.error('Name and category are required.'); return; }
    setSaving(true);
    const payload = { ...form, tags: typeof form.tags === 'string' ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : form.tags };
    try {
      if (editing) await axios.put(`${API_URL}/admin/resources/${editing}`, payload);
      else await axios.post(`${API_URL}/admin/resources`, payload);
      toast.success(editing ? 'Updated.' : 'Resource added.');
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save.'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try { await axios.delete(`${API_URL}/admin/resources/${id}`); await load(); toast.success('Deleted.'); }
    catch (e) { toast.error('Could not delete.'); }
  };

  const changeCategory = async (id, slug) => {
    try {
      const { data } = await axios.post(`${API_URL}/admin/resources/${id}/category`, { category_slug: slug });
      setResources((prev) => prev.map((r) => r.resource_id === id ? { ...r, category_slug: slug, category: data.category } : r));
      setReviewItems((prev) => prev.map((r) => r.resource_id === id ? { ...r, category_slug: slug, category: data.category } : r));
      toast.success(data.message || 'Category changed.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change category.'); }
  };

  const enrichResource = async (id) => {
    try {
      const { data } = await axios.post(`${API_URL}/admin/resources/${id}/enrich`);
      if (data.filled && Object.keys(data.filled).length) {
        setResources((prev) => prev.map((r) => r.resource_id === id ? { ...r, ...data.filled } : r));
        toast.success(data.message);
      } else { toast.info(data.message || 'Nothing new to fill.'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Enrich failed.'); }
  };

  const applyBulkCategory = async () => {
    const ids = Array.from(activeSel);
    if (!ids.length || !bulkCat) { toast.error('Select rows and a target category.'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/resources/bulk-category`, { ids, category_slug: bulkCat });
      toast.success(data.message);
      setActiveSel(new Set()); setBulkCat(''); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk re-map failed.'); } finally { setBusy(false); }
  };

  const mergePair = async (primary_id, duplicate_id, primaryName, dupName) => {
    if (!window.confirm(`Merge "${dupName}" INTO "${primaryName}"? This fills blanks on "${primaryName}" and deletes "${dupName}". Cannot be undone.`)) return;
    try {
      const { data } = await axios.post(`${API_URL}/admin/resources/merge`, { primary_id, duplicate_id });
      toast.success(data.message);
      loadDuplicates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Merge failed.'); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-sm text-gray-500 hover:text-primary flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Admin Dashboard</Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="font-serif text-3xl font-bold text-primary" data-testid="admin-resources-title">Community Resources</h1>
          <div className="flex gap-2 flex-wrap">
            <UploadResourceListModal
              categories={categories}
              isAdmin
              triggerClassName="bg-secondary hover:bg-secondary-hover text-white"
              onCommitted={() => { setViewMode('review'); loadReview(); }}
            />
            <Button className="bg-primary text-white" onClick={openNew} data-testid="admin-add-resource"><Plus className="w-4 h-4 mr-1" /> Add Resource</Button>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setViewMode('active')} data-testid="mode-active"
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${viewMode === 'active' ? 'bg-primary text-white' : 'bg-white border text-gray-600'}`}>Active Resources</button>
          <button onClick={() => setViewMode('review')} data-testid="mode-review"
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${viewMode === 'review' ? 'bg-primary text-white' : 'bg-white border text-gray-600'}`}>
            Review Queue{reviewCounts.pending_review ? ` (${reviewCounts.pending_review})` : ''}
          </button>
          <button onClick={() => setViewMode('claims')} data-testid="mode-claims"
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${viewMode === 'claims' ? 'bg-primary text-white' : 'bg-white border text-gray-600'}`}>
            Claims{claimCounts.pending ? ` (${claimCounts.pending})` : ''}
          </button>
          <button onClick={() => setViewMode('imports')} data-testid="mode-imports"
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${viewMode === 'imports' ? 'bg-primary text-white' : 'bg-white border text-gray-600'}`}>
            Import History
          </button>
          <button onClick={() => setViewMode('links')} data-testid="mode-links"
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${viewMode === 'links' ? 'bg-primary text-white' : 'bg-white border text-gray-600'}`}>
            Link Issues{linkMeta.count ? ` (${linkMeta.count})` : ''}
          </button>
          <button onClick={() => setViewMode('dupes')} data-testid="mode-dupes"
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${viewMode === 'dupes' ? 'bg-primary text-white' : 'bg-white border text-gray-600'}`}>
            Duplicates
          </button>
        </div>

        {viewMode === 'claims' ? (
          loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : (
            <div className="bg-white rounded-2xl border divide-y" data-testid="claims-list">
              {claims.length === 0 ? <p className="p-8 text-center text-gray-500">No claims yet.</p> : claims.map((c) => (
                <div key={c.claim_id} className="p-4 flex items-start justify-between gap-3" data-testid={`claim-item-${c.claim_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-primary">{c.resource_name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'pending' ? 'bg-amber-100 text-amber-700' : c.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{c.status}</span>
                      {c.temporarily_closed && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Reported closed</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">From {c.contact_name} · {c.contact_email}{c.role_title ? ` · ${c.role_title}` : ''}{c.organization ? ` · ${c.organization}` : ''}</p>
                    {c.message && <p className="text-sm text-gray-600 mt-1">"{c.message}"</p>}
                    {c.proposed_updates && Object.keys(c.proposed_updates).length > 0 && (
                      <div className="mt-2 text-xs bg-gray-50 rounded-lg p-2 space-y-0.5">
                        {Object.entries(c.proposed_updates).map(([k, v]) => (
                          <div key={k}><span className="font-semibold text-gray-700">{k}:</span> <span className="text-gray-600">{String(v)}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                  {c.status === 'pending' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approveClaim(c.claim_id)} data-testid={`claim-approve-${c.claim_id}`}>Approve & Apply</Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => rejectClaim(c.claim_id)} data-testid={`claim-reject-${c.claim_id}`}>Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : viewMode === 'imports' ? (
          loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : (
            <div className="bg-white rounded-2xl border divide-y" data-testid="imports-list">
              {imports.length === 0 ? <p className="p-8 text-center text-gray-500">No uploaded lists yet.</p> : imports.map((imp) => (
                <div key={imp.batch_id} className="p-4 flex items-start justify-between gap-3" data-testid={`import-item-${imp.batch_id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-primary truncate">{imp.filename || 'Uploaded list'}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {String(imp.created_at).slice(0, 10)} · by {imp.uploaded_by_name || imp.uploaded_by_email || 'Unknown'}
                      {' '}<span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{imp.mode === 'member' ? 'member (auto-queued)' : 'admin (reviewed)'}</span>
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{imp.total} extracted</span>
                      <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{imp.pending} pending</span>
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{imp.published} published</span>
                      {imp.duplicates_flagged > 0 && <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{imp.duplicates_flagged} possible dup</span>}
                    </div>
                  </div>
                  {imp.pending_non_duplicates > 0 && (
                    <Button size="sm" className="bg-primary text-white flex-shrink-0" onClick={() => bulkApproveImport(imp.batch_id)} data-testid={`bulk-approve-${imp.batch_id}`}>
                      Approve {imp.pending_non_duplicates} non-dup
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )
        ) : viewMode === 'links' ? (
          loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : (
            <div data-testid="link-issues-list">
              <p className="text-sm text-gray-500 mb-3">
                Links that failed or could not complete the automated check. <strong>Broken</strong> (404/gone) are hidden from the public directory until fixed or verified; <strong>Needs manual review</strong> items (redirects, outages or bot-blocks) stay public and usually still work.
                {linkMeta.last_run && <> Last run: {String(linkMeta.last_run).slice(0, 16).replace('T', ' ')}.</>}
              </p>

              {linkIssues.length > 0 && (
                <div className="p-3 mb-4 rounded-xl border bg-gray-50 flex items-center justify-between gap-3 flex-wrap sticky top-0 z-20" data-testid="link-bulk-bar">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={linkSel.size > 0 && linkSel.size === linkIssues.length} onChange={toggleLinkSelAll} data-testid="link-select-all" />
                    Select all ({linkIssues.length})
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-gray-600" data-testid="link-selected-count">{linkSel.size} selected</span>
                    <Button size="sm" disabled={!linkSel.size || busy} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40" onClick={() => bulkLink('verify')} data-testid="link-bulk-verify">Verify works</Button>
                    <Button size="sm" variant="outline" disabled={!linkSel.size || busy} className="disabled:opacity-40" onClick={() => bulkLink('recheck')} data-testid="link-bulk-recheck">Recheck</Button>
                    <Button size="sm" variant="outline" disabled={!linkSel.size || busy} className="text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40" onClick={() => bulkLink('delete')} data-testid="link-bulk-delete">Delete</Button>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl border divide-y">
                {linkIssues.length === 0 ? <p className="p-8 text-center text-gray-500">No link issues detected. 🎉</p> : linkIssues.map((r) => (
                  <div key={r.resource_id} className="p-4 flex items-start justify-between gap-3" data-testid={`link-issue-${r.resource_id}`}>
                    <div className="flex items-start gap-3 min-w-0">
                      <input type="checkbox" className="w-4 h-4 mt-1 accent-emerald-600 flex-shrink-0" checked={linkSel.has(r.resource_id)} onChange={() => toggleLinkSel(r.resource_id)} data-testid={`link-check-${r.resource_id}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-primary">{r.name}</p>
                          {r.needs_manual_review
                            ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Needs manual review</span>
                            : <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Broken · hidden from public</span>}
                          {r.link_http_status ? <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">HTTP {r.link_http_status}</span> : null}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{r.category} · {[r.city, r.state].filter(Boolean).join(', ') || (r.is_national ? 'Nationwide' : 'Local')}</p>
                        <p className="text-xs text-gray-600 mt-1">{r.link_reason}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs flex-wrap">
                          {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all" data-testid={`link-url-${r.resource_id}`}>{r.website}</a>}
                          {r.link_checked_at && <span className="text-gray-400">Checked {String(r.link_checked_at).slice(0, 16).replace('T', ' ')}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0 flex-wrap justify-end">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={() => verifyLink(r.resource_id)} data-testid={`link-verify-${r.resource_id}`} title="I checked it — the link works">Approve</Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => openEdit(r)} data-testid={`link-edit-${r.resource_id}`} title="Edit">Edit</Button>
                      <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => recheckLink(r.resource_id)} data-testid={`link-recheck-${r.resource_id}`} title="Recheck now">Recheck</Button>
                      <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteLinkResource(r.resource_id, r.name)} data-testid={`link-delete-${r.resource_id}`} title="Delete">Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : viewMode === 'dupes' ? (
          loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : (
            <div data-testid="duplicates-list">
              <p className="text-sm text-gray-500 mb-3">Likely duplicate pairs among active listings. Merging fills the primary's blank fields from the other, then deletes the other.</p>
              <div className="space-y-3">
                {dupPairs.length === 0 ? <p className="p-8 text-center text-gray-500 bg-white rounded-2xl border">No likely duplicates found. 🎉</p> : dupPairs.map((p, i) => (
                  <div key={i} className="bg-white rounded-2xl border p-4" data-testid={`dup-pair-${i}`}>
                    <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1"><Copy className="w-3 h-3" /> {p.reason}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[p.a, p.b].map((r, idx) => {
                        const other = idx === 0 ? p.b : p.a;
                        return (
                          <div key={r.resource_id} className="border rounded-xl p-3">
                            <p className="font-semibold text-primary text-sm">{r.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{r.category} · {[r.city, r.state].filter(Boolean).join(', ') || '—'}</p>
                            <p className="text-xs text-gray-400 mt-0.5 truncate">{r.website || 'no website'} · {r.phone || 'no phone'}</p>
                            <Button size="sm" className="mt-2 bg-primary text-white w-full" onClick={() => mergePair(r.resource_id, other.resource_id, r.name, other.name)} data-testid={`merge-into-${r.resource_id}`}>
                              <GitMerge className="w-3.5 h-3.5 mr-1" /> Keep this, merge other in
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : viewMode === 'review' ? (
          loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : (
            <div data-testid="review-queue-list">
              {reviewItems.length === 0 ? <p className="p-8 text-center text-gray-500 bg-white rounded-2xl border">Nothing awaiting review. 🎉</p> : (
              <>
                {/* Global bulk bar */}
                <div className="p-3 mb-4 rounded-xl border bg-gray-50 flex items-center justify-between gap-3 flex-wrap sticky top-0 z-20" data-testid="review-bulk-bar">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={selectedIds.size > 0 && selectedIds.size === reviewItems.length} onChange={toggleSelectAll} data-testid="review-select-all" />
                    Select all ({reviewItems.length})
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-600" data-testid="review-selected-count">{selectedIds.size} selected</span>
                    <Button size="sm" disabled={!selectedIds.size} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40" onClick={() => bulkAction('approve')} data-testid="bulk-approve-selected">Approve selected</Button>
                    <Button size="sm" variant="outline" disabled={!selectedIds.size} className="text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40" onClick={() => bulkAction('reject')} data-testid="bulk-reject-selected">Reject selected</Button>
                  </div>
                </div>

                {/* One block per category */}
                <div className="space-y-5">
                {reviewGroups.map(({ label, items }) => {
                  const ids = items.map((r) => r.resource_id);
                  const allSel = ids.every((id) => selectedIds.has(id));
                  const isUncat = label === 'Uncategorized';
                  return (
                  <div key={label} className={`rounded-2xl border overflow-hidden ${isUncat ? 'border-amber-300' : ''}`} data-testid={`review-group-${label}`}>
                    <div className={`p-3 flex items-center justify-between gap-3 flex-wrap ${isUncat ? 'bg-amber-50' : 'bg-primary/5'}`}>
                      <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" style={{ color: isUncat ? '#92400e' : '#0f766e' }}>
                        <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={allSel} onChange={() => toggleCategory(items)} data-testid={`cat-select-${label}`} />
                        {label} <span className="font-normal text-gray-500">({items.length} waiting)</span>
                        {isUncat && <span className="text-xs text-amber-700">— assign a category before approving</span>}
                      </label>
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={() => runBulk(ids, 'approve', label)} data-testid={`cat-approve-${label}`}>Approve all ({items.length})</Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8" onClick={() => runBulk(ids, 'reject', label)} data-testid={`cat-reject-${label}`}>Reject all</Button>
                      </div>
                    </div>
                    <div className="divide-y bg-white">
                      {items.map((r) => (
                      <div key={r.resource_id} className="p-4 flex items-start justify-between gap-3" data-testid={`review-item-${r.resource_id}`}>
                        <div className="flex items-start gap-3 min-w-0">
                          <input type="checkbox" className="w-4 h-4 mt-1 accent-emerald-600 flex-shrink-0" checked={selectedIds.has(r.resource_id)} onChange={() => toggleSelect(r.resource_id)} data-testid={`review-check-${r.resource_id}`} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-primary">{r.name}</p>
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{r.status?.replace('_', ' ')}</span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{r.service_area_type || '—'}</span>
                              {r.possible_duplicate && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`review-dup-${r.resource_id}`}>Possible duplicate{r.duplicate_of?.name ? `: ${r.duplicate_of.name}` : ''}</span>}
                              {r.submitted_via === 'ai_upload' && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">AI upload</span>}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{[r.city, r.county, r.state].filter(Boolean).join(', ') || 'No location'}</p>
                            {r.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{r.description}</p>}
                            <div className="flex items-center gap-3 mt-1.5 text-xs flex-wrap">
                              {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">Open source</a>}
                              {r.submitter_email && <span className="text-gray-400">by {r.submitter_email}</span>}
                              {isUncat && (
                                <Select value="" onValueChange={(v) => changeCategory(r.resource_id, v)}>
                                  <SelectTrigger className="w-48 h-7 text-xs" data-testid={`review-setcat-${r.resource_id}`}><SelectValue placeholder="Assign category…" /></SelectTrigger>
                                  <SelectContent className="max-h-72">{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}</SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approveResource(r.resource_id)} data-testid={`approve-${r.resource_id}`}>Approve</Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => rejectResource(r.resource_id)} data-testid={`reject-${r.resource_id}`}>Reject</Button>
                        </div>
                      </div>
                      ))}
                    </div>
                  </div>
                  );
                })}
                </div>
              </>
              )}
            </div>
          )
        ) : (
        <>
        <div className="flex gap-2 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="pl-9" placeholder="Search resources" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-resource-search" />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-56" data-testid="admin-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {activeSel.size > 0 && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-cat-bar">
            <span className="text-sm font-semibold text-primary" data-testid="bulk-cat-count">{activeSel.size} selected</span>
            <div className="flex items-center gap-2">
              <Select value={bulkCat} onValueChange={setBulkCat}>
                <SelectTrigger className="w-56 h-9 text-xs bg-white" data-testid="bulk-cat-select"><SelectValue placeholder="Move to category…" /></SelectTrigger>
                <SelectContent className="max-h-72">{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" className="bg-primary text-white disabled:opacity-40" disabled={!bulkCat || busy} onClick={applyBulkCategory} data-testid="bulk-cat-apply">Move {activeSel.size}</Button>
              <Button size="sm" variant="outline" onClick={() => setActiveSel(new Set())} data-testid="bulk-cat-clear">Clear</Button>
            </div>
          </div>
        )}

        {loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : (
          <div className="bg-white rounded-2xl border divide-y">
            {resources.length === 0 ? <p className="p-8 text-center text-gray-500">No resources.</p> : resources.map((r) => (
              <div key={r.resource_id} className="p-4 flex items-center justify-between gap-3" data-testid={`admin-resource-${r.resource_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <input type="checkbox" className="w-4 h-4 accent-primary flex-shrink-0" checked={activeSel.has(r.resource_id)} onChange={() => toggleActive(r.resource_id)} data-testid={`admin-select-${r.resource_id}`} />
                  <div className="min-w-0">
                    <p className="font-semibold text-primary truncate">{r.name}</p>
                    <p className="text-xs text-gray-500">{r.category} · {r.is_national ? 'Nationwide' : [r.city, r.state].filter(Boolean).join(', ') || 'Local'}{!r.phone || !r.address ? <span className="text-amber-600"> · missing {[!r.phone && 'phone', !r.address && 'address'].filter(Boolean).join(' & ')}</span> : null}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Select value={r.category_slug} onValueChange={(v) => changeCategory(r.resource_id, v)}>
                    <SelectTrigger className="w-44 h-9 text-xs" data-testid={`admin-recat-${r.resource_id}`}>
                      <SelectValue placeholder="Change category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {(!r.phone || !r.address) && r.website && (
                    <button className="text-gray-400 hover:text-secondary p-2" title="Enrich phone/address from website" onClick={() => enrichResource(r.resource_id)} data-testid={`admin-enrich-${r.resource_id}`}><Sparkles className="w-4 h-4" /></button>
                  )}
                  <button className="text-gray-400 hover:text-primary p-2" onClick={() => openEdit(r)} data-testid={`admin-edit-${r.resource_id}`}><Pencil className="w-4 h-4" /></button>
                  <button className="text-gray-400 hover:text-red-600 p-2" onClick={() => remove(r.resource_id)} data-testid={`admin-delete-${r.resource_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit Resource' : 'Add Resource'}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label className="text-xs text-gray-500 mb-1 block">Name *</Label><Input value={form.name} onChange={(e) => setF('name', e.target.value)} data-testid="admin-form-name" /></div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Category *</Label>
                <Select value={form.category_slug} onValueChange={(v) => setF('category_slug', v)}>
                  <SelectTrigger data-testid="admin-form-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-gray-500 mb-1 block">Phone</Label><Input value={form.phone} onChange={(e) => setF('phone', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-gray-500 mb-1 block">Website</Label><Input value={form.website} onChange={(e) => setF('website', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-gray-500 mb-1 block">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setF('description', e.target.value)} /></div>
              <div><Label className="text-xs text-gray-500 mb-1 block">City</Label><Input value={form.city} onChange={(e) => setF('city', e.target.value)} /></div>
              <div><Label className="text-xs text-gray-500 mb-1 block">State</Label><Input value={form.state} onChange={(e) => setF('state', e.target.value)} /></div>
              <div><Label className="text-xs text-gray-500 mb-1 block">ZIP</Label><Input value={form.zip_code} onChange={(e) => setF('zip_code', e.target.value)} /></div>
              <div><Label className="text-xs text-gray-500 mb-1 block">Hours</Label><Input value={form.hours} onChange={(e) => setF('hours', e.target.value)} /></div>
              <div><Label className="text-xs text-gray-500 mb-1 block">Eligibility</Label><Input value={form.eligibility} onChange={(e) => setF('eligibility', e.target.value)} /></div>
              <div><Label className="text-xs text-gray-500 mb-1 block">Languages</Label><Input value={form.languages} onChange={(e) => setF('languages', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-gray-500 mb-1 block">Tags (comma-separated)</Label><Input value={Array.isArray(form.tags) ? form.tags.join(', ') : form.tags} onChange={(e) => setF('tags', e.target.value)} /></div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer sm:col-span-2"><input type="checkbox" checked={form.is_national} onChange={(e) => setF('is_national', e.target.checked)} data-testid="admin-form-national" /> Nationwide resource</label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-primary text-white" onClick={save} disabled={saving} data-testid="admin-save-resource">{saving ? 'Saving…' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminResources;
